/* Shared funnel contract for both storefront experiments. See docs/swapwear-funnel.md. */
(function () {
  'use strict';
  const prefix = 'sw_';
  const storageKey = 'swapwear:funnel:v1';
  const ttl = 30 * 60 * 1000;
  const script = document.currentScript;
  const theme = script?.dataset.funnelTheme || 'unknown';
  const debug = new URLSearchParams(location.search).has('sw_funnel_debug');
  const registry = () => window.__initialData?.bases || [];
  const base = item => registry().find(row => row.handle === item?.handle);
  const style = item => base(item)?.modelKey?.split('_')[0] || item?.handle || '';
  const properties = item => Array.isArray(item?.properties) ? Object.fromEntries(item.properties) : (item?.properties || {});
  const panel = item => /^(swappable patch|swappable panel|art panel)$/i.test(item?.product_type || item?.type || '') &&
    !/mystery|custom.patch|blank/i.test([item?.handle, item?.title, item?.product_title].join(' ')) &&
    !properties(item).custom_id;
  const quantity = value => Math.max(0, Math.trunc(Number(value) || 0));
  const countPanels = cart => (cart?.items || []).filter(panel).reduce((n, item) => n + quantity(item.quantity), 0);
  const hasBase = cart => (cart?.items || []).some(item => base(item) && quantity(item.quantity));
  let state = {};
  let activeContext = {};
  let lastCart = window.__initialData?.cart;
  let manualDrawerAt = 0;
  try { state = JSON.parse(sessionStorage.getItem(storageKey)) || {}; } catch (_) {}
  const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const refresh = () => {
    if (!state.at || !state.seen || !state.styles || !state.selectedStyles || Date.now() - state.at > ttl) state = { seen: {}, styles: {}, selectedStyles: {}, at: Date.now() };
  };
  const save = () => { try { sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch (_) {} };
  const info = item => ({ product_id: String(item?.product_id || item?.id || ''), product_handle: item?.handle || '',
    base_style: base(item) ? style(item) : '', variant_id: String(item?.variant_id || ''), product_kind: base(item) ? 'base' : 'panel' });
  function emit(name, data = {}, unique = '') {
    if (!allowed()) return false;
    refresh();
    if (unique && state.seen[unique]) return false;
    if (unique) state.seen[unique] = true;
    state.at = Date.now();
    let experimentContext = {};
    let experimentPreview = false;
    try {
      const experiments = window.igData?.user?.getExperiments?.() || [];
      experimentPreview = experiments.some(experiment => experiment.isPreview);
      const experiment = experiments.length === 1 ? experiments[0] : null;
      const group = experiment && window.igData.user.getTestGroup(experiment.id);
      if (group) experimentContext = { experiment_id: experiment.id, experiment_group: group.name };
    } catch (_) {}
    const properties = { schema_version: 1, theme_variant: theme, ...experimentContext, ...data };
    save();
    if (/^(localhost|127\.0\.0\.1)$/.test(location.hostname || '') || window.Shopify?.designMode || experimentPreview) {
      if (debug) console.debug('[SwapWear funnel]', JSON.stringify({ event: prefix + name, properties }));
      window.dispatchEvent(new CustomEvent('swapwear:funnel-debug', { detail: { event: prefix + name, properties } }));
      return true;
    }
    try {
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: prefix + name, properties });
    } catch (_) {}
    // gtag is not guaranteed when Google runs inside Shopify's pixel sandbox.
    // Publish there too; the documented custom-pixel bridge can subscribe.
    try {
      if (typeof window.gtag === 'function') window.gtag('event', prefix + name, properties);
      else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: prefix + name, ...properties });
      }
      window.Shopify?.analytics?.publish?.('swapwear_funnel', { event_name: prefix + name, ga_transport: typeof window.gtag === 'function' ? 'gtag' : 'pixel_or_gtm', ...properties });
    } catch (_) {}
    return true;
  }
  function viewed(item, context = {}) {
    if (!base(item)) return;
    const key = `${context.placement || 'other'}:${item.handle}`;
    emit('base_viewed', { ...info(item), ...context }, 'view:' + key);
    if (!allowed()) return;
    refresh();
    const family = style(item);
    if (emit('base_style_viewed', { ...info(item), ...context }, 'style:' + family)) state.styles[family] = true;
    if (Object.keys(state.styles).length >= 2) emit('base_explored_multiple', context, 'multiple-styles');
    save();
  }
  function selectBase(item, previousHandle, element) {
    if (!base(item)) return;
    const placement = element?.closest?.('[data-funnel-base]')?.dataset.funnelBase || 'base_picker';
    activeContext = { placement, selection_source: 'customer_selected', product_handle: item.handle };
    emit('base_selected', { ...info(item), ...activeContext });
    if (allowed()) {
      refresh();
      state.selection = { ...activeContext, at: Date.now() };
      emit('base_style_selected', { ...info(item), ...activeContext }, 'selected-style:' + style(item));
      state.selectedStyles[style(item)] = true;
      save();
    }
    const previous = base({ handle: previousHandle });
    if (previous && previous.handle !== item.handle && style(previous) === style(item) &&
      registry().filter(row => style(row) === style(item)).length > 1) {
      emit('base_color_selected', { ...info(item), ...activeContext });
    }
  }
  function lineEvent(item, delta, context) {
    const kind = base(item) ? 'base' : panel(item) ? 'panel' : '';
    if (!kind || !delta) return;
    if (kind === 'base' && delta > 0) viewed(item, { ...context, exposure_reason: 'confirmed_add' });
    emit(kind + (delta > 0 ? '_added' : '_removed'), { ...context, ...info(item), quantity: Math.abs(delta) });
    // Intelligems event properties are not numeric metric inputs. Unit events
    // make event-count / visitor meaningful even when adding quantity > 1.
    if (kind === 'panel') for (let n = 0; n < Math.abs(delta); n++) {
      emit(delta > 0 ? 'panel_unit_added' : 'panel_unit_removed', { ...context });
    }
  }
  function cartMilestones(cart, context = {}) {
    if (!cart?.items) return;
    lastCart = cart;
    const panels = countPanels(cart);
    const withBase = hasBase(cart);
    emit('cart_composition', { ...context, panel_units: panels, has_base: withBase });
    if (withBase && panels > 0) emit('base_panel_attached', context, 'attached');
    for (const threshold of [1, 2, 3, 4, 5]) if (panels >= threshold) {
      emit('cart_panels_' + threshold, context, 'panels:' + threshold);
    }
    const journey = window.swapwearBundleJourney;
    if (journey?.hasIntent?.() && withBase && Number(journey.target()) > 0 && panels >= Number(journey.target())) {
      emit('set_completed', { ...context, target_panels: journey.target() }, 'set:' + journey.intent()?.id);
    }
  }
  function mutation(endpoint, body, data, before, context = {}) {
    if (!data || typeof data !== 'object') return;
    if (/\/add\.js$/.test(endpoint) && !Object.hasOwn(data, 'item_count')) {
      const lines = Array.isArray(data.items) ? data.items : [data];
      const requested = body?.items || (body?.id ? [body] : []);
      const next = { ...(before || lastCart), items: [...(before?.items || lastCart?.items || [])] };
      for (const line of lines) {
        const sameVariant = requested.filter(row => String(row.id) === String(line.variant_id || line.id));
        const normalized = item => JSON.stringify(Object.entries(properties(item)).sort());
        const additions = sameVariant.length > 1 ? sameVariant.filter(row => normalized(row) === normalized(line)) : sameVariant;
        const added = additions.reduce((sum, row) => sum + quantity(row.quantity ?? 1), 0);
        if (!added || !line.id) continue;
        lineEvent(line, added, context);
        const index = next.items.findIndex(row => row.key === line.key);
        if (index >= 0) next.items[index] = line;
        else next.items.push(line);
      }
      cartMilestones(next, context);
    } else if (Array.isArray(data.items) && before?.items) {
      const ids = new Set([...before.items, ...data.items].map(row => row.key));
      for (const key of ids) {
        const old = before.items.find(row => row.key === key);
        const current = data.items.find(row => row.key === key);
        lineEvent(current || old, quantity(current?.quantity) - quantity(old?.quantity), context);
      }
      cartMilestones(data, context);
    }
  }
  const scopeData = element => {
    const root = element?.closest?.('[data-funnel-base]');
    if (!root || !window.Alpine) return null;
    const data = window.Alpine.$data(root);
    const item = data?.product;
    if (!base(item)) return null;
    const pending = state.selection;
    const selection_source = pending?.product_handle === item.handle && Date.now() - pending.at < ttl ? 'customer_selected' :
      new URLSearchParams(location.search).has('base') || new URLSearchParams(location.search).has('variant') ? 'link_preselected' :
      root.dataset.funnelBase === 'pdp' ? 'navigation' : 'default';
    return { root, data, item, context: { placement: root.dataset.funnelBase, selection_source, product_handle: item.handle } };
  };
  function action(element, actionName, extra = {}) {
    const scope = scopeData(element);
    if (!scope) return;
    activeContext = scope.context;
    viewed(scope.item, { ...scope.context, exposure_reason: 'interaction' });
    emit(actionName, { ...info(scope.item), ...scope.context, ...extra });
  }
  window.swapwearFunnel = { emit, viewed, selectBase, mutation, cartMilestones,
    cart: () => lastCart,
    syncCart: cart => { lastCart = cart; },
    context: () => ({ placement: /\/products\//.test(location.pathname) ? 'pdp' : /\/collections\//.test(location.pathname) ? 'collection' : 'homepage_builder', ...activeContext }),
    drawerOpened() {
      const open_source = Date.now() - manualDrawerAt < 1500 ? 'manual' : 'automatic';
      const data = { open_source, panel_units: countPanels(lastCart) };
      emit('cart_drawer_opened', data);
      emit('cart_reviewed', { ...data, surface: 'drawer' }, 'cart-review');
      if (open_source === 'manual') emit('cart_drawer_opened_manual', data);
      manualDrawerAt = 0;
    },
    manualDrawer() { manualDrawerAt = Date.now(); }
  };
  // Read the currently rendered product, not a preselected/offscreen thumbnail.
  const dwell = new Map();
  function scan() {
    if (document.visibilityState !== 'visible' || !allowed()) { dwell.clear(); return; }
    if (/\/cart\/?$/.test(location.pathname)) emit('cart_reviewed', { surface: 'cart_page', panel_units: countPanels(lastCart) }, 'cart-review');
    const roots = [...document.querySelectorAll('[data-funnel-base]')];
    for (const root of dwell.keys()) if (!roots.includes(root)) dwell.delete(root);
    for (const root of roots) {
      const scope = scopeData(root);
      const gallery = root.querySelector('.bundle-configurator__gallery, .product-main__media-column, .build-base-media-column');
      const image = gallery && [...gallery.querySelectorAll('img')].find(img => {
        const rect = img.getBoundingClientRect();
        return img.complete && img.naturalWidth > 0 && rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < innerWidth;
      });
      const rect = image?.getBoundingClientRect();
      const visibleHeight = rect ? Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0)) : 0;
      const visibleWidth = rect ? Math.max(0, Math.min(rect.right, innerWidth) - Math.max(rect.left, 0)) : 0;
      const ready = scope && !scope.data.loading && !scope.data.mediaLoading &&
        root.getAttribute('aria-busy') !== 'true' && rect && visibleWidth >= Math.min(rect.width, innerWidth) * .5 &&
        visibleHeight >= Math.min(rect.height, innerHeight) * .5;
      if (!ready) { dwell.delete(root); continue; }
      const prior = dwell.get(root);
      if (!prior || prior.handle !== scope.item.handle) dwell.set(root, { handle: scope.item.handle, start: Date.now() });
      else if (Date.now() - prior.start >= 1000) {
        activeContext = scope.context;
        viewed(scope.item, { ...scope.context, exposure_reason: 'visible_1s' });
      }
    }
  }
  document.addEventListener('visibilitychange', () => dwell.clear());
  document.addEventListener('click', event => {
    const control = event.target.closest?.('button, input, select, a');
    if (!control || control.disabled) return;
    if (control.matches('#size-guides > button')) action(control, 'base_size_guide_opened');
    if (control.closest('.bundle-configurator__sizes')) action(control, 'base_size_selected', { size: control.textContent.trim() });
    const option = control.closest('[data-funnel-option]');
    if (option && control.hasAttribute('data-funnel-value')) {
      const name = option.dataset.funnelOption;
      if (name === 'size') action(control, 'base_size_selected', { size: control.dataset.funnelValue });
      else if (/^(color|colour)$/.test(name) && Number(option.dataset.funnelOptionCount) > 1) action(control, 'base_color_selected');
    }
    const scope = scopeData(control);
    if (scope) activeContext = scope.context;
    else if (control.closest('.brand-layer-cart-drawer')) activeContext = { placement: 'cart_drawer' };
  }, true);
  document.addEventListener('change', event => {
    const option = event.target.closest?.('[data-funnel-option]');
    if (!event.target.matches?.('select') || !event.target.value || !option) return;
    if (option.dataset.funnelOption === 'size') action(event.target, 'base_size_selected', { size: event.target.value });
    else if (/^(color|colour)$/.test(option.dataset.funnelOption) && Number(option.dataset.funnelOptionCount) > 1) action(event.target, 'base_color_selected');
  });
  window.addEventListener('click', event => {
    const chip = event.target.closest?.('[data-funnel-chip]');
    const scope = chip?.closest?.('[data-funnel-panel-filters]');
    if (!scope || chip.disabled || chip.getAttribute('aria-disabled') === 'true' || event.target.disabled) return;
    const kind = chip.dataset.funnelChip;
    // Labels forward a second click to their radio. Count only that activation.
    if (kind === 'category' && !event.target.matches?.('input[type="radio"]')) return;
    const data = {
      placement: scope.dataset.funnelPanelFilters,
      collection_handle: scope.dataset.funnelCollection || '',
      chip_type: kind,
      chip_value: chip.dataset.funnelChipValue || '',
      set_mode: Boolean(window.swapwearBundleJourney?.isArtStep?.())
    };
    emit('panel_filter_chip_clicked', data);
    if (kind === 'category') emit('panel_category_chip_clicked', data);
  });
  window.addEventListener('swapwear:panel-commerce-action', event => {
    if (event.detail?.action === 'view') {
      activeContext = { placement: 'panel_modal' };
      emit('panel_viewed', { product_id: event.detail.product_id, product_handle: event.detail.product_handle, placement: 'panel_modal' }, 'panel:' + (event.detail.product_id || event.detail.product_handle));
    }
  });
  window.addEventListener('cart:updated', event => { lastCart = event.detail?.cart || lastCart; });
  document.addEventListener('visitorConsentCollected', () => {
    if (!allowed()) { state = {}; try { sessionStorage.removeItem(storageKey); } catch (_) {} }
  });
  // Shopify owns regional consent decisions. Do not invent consent or bypass it.
  if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
  }
  setInterval(() => { try { scan(); } catch (_) { /* Never interrupt shopping. */ } }, 400);
})();
